/**
 * 명치사 회원가입 약관 본문.
 * - 필수 4건 + 선택 1건
 * - 한국 개인정보보호법 / 정보통신망법 / 약관규제법 기준 표준 문구 + 명치사 도메인(치과 종사자, 면허번호) 반영
 * - 운영 시 법무 검토 권장. 본 문구는 실무 베이스라인.
 */

import {
    WITHDRAWAL_RETENTION_EFFECTIVE_DATE,
    WITHDRAWAL_RETENTION_EFFECTIVE_ISO,
    WITHDRAWAL_RETENTION_NOTICE_DATE,
} from '@/pages/Legal/withdrawalAmendmentDates';
import {
    PROMISE_RECORD_EFFECTIVE_DATE,
    PROMISE_RECORD_EFFECTIVE_ISO,
    PROMISE_RECORD_NOTICE_DATE,
} from '@/pages/Legal/promiseRecordAmendmentDates';

export const SERVICE_NAME = '명치사';
export const SERVICE_DOMAIN = 'www.myungchisa.com';
export const CONTACT_EMAIL = 'support@myungchisa.com';

/**
 * 약관 본문 버전.
 * 약관 본문(TERMS 배열)을 변경할 때마다 이 값을 갱신한다.
 * 백엔드 user_agreements.terms_version 컬럼에 저장되고, 약관 화면에는 "시행일 / 버전" 으로 노출된다.
 * 제3조 제2항이 개정 사전 공지 기간을 규정하므로 게시일이 아니라 **개정 시행일**을 값으로 쓴다.
 */
/**
 * 가장 늦은 개정 시행일 — 면허번호 이용 목적 개정(2026-10-12), 제10조 제2항 개정(탈퇴 후 복구 기간·익명 처리, withdrawalAmendmentDates.ts),
 * 제6조의2·제9조 제3항 신설(약속 기록 수집·공개, promiseRecordAmendmentDates.ts) 중 늦은 날.
 */
export const TERMS_VERSION: string = ['2026-10-12', WITHDRAWAL_RETENTION_EFFECTIVE_ISO, PROMISE_RECORD_EFFECTIVE_ISO].sort().reverse()[0];

export type TermsId =
    | 'service'           // 서비스 이용약관 [필수]
    | 'privacy'           // 개인정보 수집·이용 동의 [필수]
    | 'thirdParty'        // 개인정보 제3자 제공 동의 [필수]
    | 'sensitive'         // 민감정보(면허) 처리 동의 [필수]
    | 'resumePublic'      // 이력서·프로필 공개 동의 [선택]
    | 'recommendation'    // 맞춤 추천 알고리즘 활용 동의 [선택]
    | 'pushAdvertising';  // 앱 푸시 광고성 알림 수신 동의 [선택] — user_push_agreements 광고성 토픽에 저장

export interface TermsItem {
    id: TermsId;
    title: string;
    required: boolean;
    summary: string;
    content: string;
    /** 내정보수정의 "약관·동의 설정" 토글에서 제외 (별도 설정 화면에서 관리하는 항목) */
    hiddenInProfile?: boolean;
}

export const TERMS: TermsItem[] = [
    {
        id: 'service',
        title: '서비스 이용약관',
        required: true,
        summary: '명치사가 제공하는 채용·세미나·커뮤니티 서비스 이용에 관한 기본 약관입니다.',
        content: `
제1조 (목적)
본 약관은 ${SERVICE_NAME}(이하 "회사"라 합니다)가 운영하는 ${SERVICE_DOMAIN} 및 관련 모바일 서비스(이하 "서비스")의 이용과 관련하여 회사와 이용자 간의 권리, 의무 및 책임사항, 기타 필요한 사항을 규정함을 목적으로 합니다.

제2조 (용어의 정의)
1. "서비스"라 함은 회사가 제공하는 치과 종사자 대상 채용공고 게시·지원, 세미나 등록·신청, 전문가 라운지·속닥속닥 커뮤니티 등 일체의 서비스를 말합니다.
2. "회원"이라 함은 본 약관에 동의하고 회사가 정한 절차에 따라 회원가입을 완료한 자로서, 회사가 제공하는 서비스를 이용할 수 있는 자를 말합니다.
3. "일반회원"이라 함은 채용 지원, 세미나 참가, 커뮤니티 이용을 목적으로 가입한 개인 회원을 말합니다.
4. "병/의원 회원"이라 함은 채용공고 등록, 지원자 관리, 세미나 개설 등을 목적으로 가입한 의료기관 또는 사업자 회원을 말합니다.

제3조 (약관의 효력 및 변경)
1. 본 약관은 회사가 서비스 화면에 게시함으로써 효력이 발생합니다.
2. 회사는 관련 법령을 위배하지 않는 범위에서 본 약관을 개정할 수 있으며, 개정 시 적용 일자 및 개정 사유를 명시하여 최소 7일 전(불리한 변경의 경우 30일 전)부터 적용 일자 전일까지 서비스 내에 공지합니다.
3. 회원이 개정 약관에 동의하지 않는 경우 회원 탈퇴를 요청할 수 있으며, 공지 기간 내에 거부 의사를 표시하지 않으면 변경된 약관에 동의한 것으로 봅니다.

제4조 (회원가입)
1. 회원가입은 이용자가 약관에 동의하고 회사가 정한 가입 양식에 정보를 기입한 후, 회사가 승낙함으로써 체결됩니다.
2. 회사는 다음 각 호에 해당하는 경우 가입 신청을 거절하거나 사후에 회원 자격을 박탈할 수 있습니다.
  - 타인의 명의 또는 정보를 도용한 경우
  - 만 14세 미만의 아동이 법정대리인의 동의 없이 가입을 시도하는 경우
  - 허위 정보를 기재하거나 회사가 제시하는 내용을 기재하지 않은 경우
  - 사회의 안녕질서 또는 미풍양속을 저해할 목적으로 신청한 경우
  - 부정한 용도(스팸, 상업적 도배, 허위 채용공고 등)로 서비스를 이용하고자 하는 경우

제5조 (회원의 의무)
1. 회원은 본 약관과 관계 법령, 회사가 공지하는 사항을 준수하여야 합니다.
2. 회원은 자신의 계정(아이디·비밀번호) 관리에 대한 책임을 부담하며, 이를 제3자에게 양도·대여할 수 없습니다.
3. 회원은 서비스 이용 중 다음 각 호의 행위를 하여서는 안 됩니다.
  - 허위 채용공고·이력서·세미나 정보 게시
  - 면허번호 등 자격 정보의 위조·도용
  - 다른 회원의 개인정보(이력서 등) 무단 수집·저장·유포
  - 회사의 사전 동의 없는 영리 활동, 광고, 다단계 권유
  - 욕설·차별·혐오 표현, 명예훼손, 음란물 게시
  - 서비스 운영을 방해하는 일체의 행위

제6조 (서비스의 제공)
1. 회사는 회원에게 다음의 서비스를 제공합니다.
  - 채용공고 검색·지원·즐겨찾기
  - 세미나 등록·신청·후기
  - 전문가 라운지(질문/칼럼/뉴스/자료) 게시·열람
  - 속닥속닥 익명 커뮤니티 게시·댓글
  - 이력서 작성·관리·공유
  - 회사가 정하는 기타 서비스
2. 회사는 시스템 점검, 통신 장애, 천재지변 등 불가피한 사유로 서비스 제공을 일시 중단할 수 있으며, 이 경우 사전에 공지합니다. 다만 부득이한 경우 사후에 공지할 수 있습니다.

제6조의2 (약속 기록)
1. 회사는 채용 과정에서 회원 사이에 확정된 근무·면접·첫 출근 약속(이하 "약속"이라 합니다)이 끝나면 양쪽 회원에게 진행 결과를 묻고, 그 결과(근무 완료, 노쇼, 지각, 시작 24시간 안 취소 등, 이하 "약속 기록"이라 합니다)를 기록합니다. 일반회원과 병/의원 회원에게는 같은 기준을 적용합니다.
2. 양쪽 회원의 답이 다르거나 회원이 기록에 이의를 제기한 경우, 각 회원은 7일 안에 설명과 자료를 남길 수 있으며 회사는 대화방 기록 등을 확인하여 판단합니다. 사실을 확인할 수 없는 경우에는 어느 쪽에도 약속 기록을 남기지 않습니다.
3. 회사는 최근 12개월의 약속 기록을 집계한 결과(확정된 약속 건수, 약속을 지킨 건수, 노쇼·지각·시작 24시간 안 취소 건수 등)와 병/의원 회원이 지원서를 받고 처음 답하기까지 걸린 시간(최근 6개월 평균, 이하 "응답 시간"이라 합니다)을 다음 각 호의 회원에게 보여 줍니다. 이때 상대 회원의 이름, 약속 날짜 등 개별 약속을 알아볼 수 있는 정보는 보여 주지 않습니다.
  - 병/의원 회원의 약속 기록 집계와 응답 시간: 그 병/의원의 채용공고에 지원하거나 지원한 일반회원(응답 시간은 채용공고 상세에서 누구나 볼 수 있습니다)
  - 일반회원의 약속 기록 집계: 그 회원이 지원한 병/의원 회원
4. 다툼 중이거나 회사가 확인 중인 약속 기록과 회사의 판단으로 지워진 약속 기록은 집계에서 제외하며, 같은 병/의원 회원과 같은 일반회원 사이에 30일 안에 생긴 여러 약속은 1건으로 셉니다.
5. 회원은 지원 내역(일반회원) 또는 채용공고 관리·지원자 관리(병/의원 회원) 화면에서 지원 건별 약속 기록과 상대 회원에게 보이는 모양을 확인할 수 있으며, 기록이 사실과 다른 경우 대화방 또는 문의하기를 통해 다시 확인을 요청할 수 있습니다.

제7조 (서비스 이용 제한)
회사는 회원이 본 약관 제5조 또는 관계 법령을 위반한 경우 사전 통지 없이 게시물 삭제, 일부 기능 제한, 일시 또는 영구 이용 정지, 회원 자격 박탈 등의 조치를 취할 수 있습니다.

제8조 (게시물의 관리)
1. 회원이 게시한 게시물(채용공고, 이력서, 댓글, 후기 등)의 저작권은 해당 회원에게 귀속되며, 회원은 회사에게 서비스 운영·홍보·개선 목적으로 게시물을 무상으로 이용할 수 있는 권리를 허락합니다.
2. 회사는 다음 각 호에 해당하는 게시물을 사전 통지 없이 삭제·이동·차단할 수 있습니다.
  - 타인의 권리(저작권·초상권·명예 등)를 침해하는 게시물
  - 음란·폭력·차별·혐오 등 공서양속에 반하는 게시물
  - 상업적 광고 또는 영리 목적의 게시물
  - 허위 사실 또는 사기성 게시물
  - 기타 관계 법령 또는 본 약관에 위반되는 게시물

제9조 (개인정보 보호)
1. 회사는 회원의 개인정보를 관계 법령에 따라 보호하기 위해 노력하며, 개인정보의 수집·이용·제공·파기 등에 관한 사항은 별도의 개인정보처리방침에 따릅니다.
2. 회사는 회원이 입력한 면허번호를 본인 확인 및 면허 인증 목적으로만 이용하며, 그 밖의 목적으로 이용하거나 회원의 동의 없이 제3자에게 제공하지 않습니다.
3. 회사는 제6조의2에 따른 약속 기록을 채용 과정의 신뢰 확보와 약속 기록에 관한 다툼 확인 목적으로만 이용하며, 같은 조 제3항에서 정한 범위를 넘어 제3자에게 제공하지 않습니다. 탈퇴한 회원과의 약속 기록은 상대 회원의 집계에서도 제외합니다.

제10조 (회원 탈퇴 및 자격 상실)
1. 회원은 언제든지 서비스 내 '회원 탈퇴' 메뉴를 통해 가입을 해지할 수 있으며, 회사는 즉시 처리합니다.
2. 탈퇴 후 30일 동안은 같은 계정으로 로그인해 계정을 복구할 수 있으며, 30일이 지나면 회원의 개인정보는 개인을 알아볼 수 없도록 익명 처리됩니다. 면허 인증 신청 정보, 설문 응답, AI 질문 대화는 탈퇴 즉시 삭제되어 복구되지 않습니다. 회원이 작성한 게시글·댓글과 병·의원에 제출한 지원서·이력서는 탈퇴 후에도 삭제되지 않습니다. 단, 관계 법령(전자상거래법, 통신비밀보호법 등)에 따른 보존 의무가 있는 정보는 해당 기간 동안 보관 후 파기합니다.

제11조 (책임의 제한)
1. 회사는 회원 간 또는 회원과 제3자(병원, 세미나 운영자 등) 간에 서비스를 매개로 발생한 분쟁에 대해 개입할 의무가 없으며, 이로 인한 손해를 배상할 책임을 부담하지 않습니다.
2. 회사는 회원이 게시한 정보(이력서, 채용공고, 커뮤니티 글 등)의 신뢰도·정확성에 대해 보증하지 않으며, 이용자가 해당 정보를 신뢰하여 발생한 손해에 대해 책임을 부담하지 않습니다.
3. 회사는 천재지변, 정전, 시스템 장애, 통신 장애 등 불가항력으로 인한 서비스 제공 중단에 대하여 책임을 부담하지 않습니다.

제12조 (준거법 및 관할법원)
1. 본 약관은 대한민국 법령에 따라 해석됩니다.
2. 본 약관 또는 서비스 이용과 관련한 분쟁에 대하여는 민사소송법상의 관할법원을 제1심 관할법원으로 합니다.

부 칙
본 약관은 2026년 6월 1일부터 시행합니다.
본 개정 약관(면허번호 이용 목적 한정 명시)은 2026년 9월 12일에 공지하였으며, 제3조 제2항의 사전 공지 기간(불리한 변경 30일 전)에 따라 2026년 10월 12일부터 시행합니다. 시행일 전까지는 종전 약관이 적용됩니다.
제10조 제2항(탈퇴 후 30일 복구 기간과 익명 처리) 개정은 ${WITHDRAWAL_RETENTION_NOTICE_DATE}에 게시하고 ${WITHDRAWAL_RETENTION_EFFECTIVE_DATE}부터 시행합니다.
제6조의2(약속 기록) 및 제9조 제3항 신설은 ${PROMISE_RECORD_NOTICE_DATE}에 게시하고 ${PROMISE_RECORD_EFFECTIVE_DATE}부터 시행합니다.
        `.trim(),
    },
    {
        id: 'privacy',
        title: '개인정보 수집·이용 동의',
        required: true,
        summary: '회원 식별 및 서비스 제공을 위한 개인정보 수집·이용에 동의해 주셔야 합니다.',
        content: `
${SERVICE_NAME}(이하 "회사")는 「개인정보 보호법」 제15조에 따라 회원가입 및 서비스 제공을 위해 아래와 같이 개인정보를 수집·이용합니다.

1. 수집하는 개인정보 항목
  가. 일반회원
    [필수]
    - 이름, 휴대전화번호, 이메일, 비밀번호, 닉네임, 직무(치과위생사·치과기공사·치과의사·학생 등)
    [선택 — 서비스 이용 중 입력]
    - 생년월일, 성별, 지역(거주지/관심 지역), 학력, 경력, 자기소개, 프로필 사진
  나. 병/의원 회원
    [필수]
    - 사업자등록번호, 의료기관 명칭, 대표자명, 담당자명·연락처·이메일, 의료기관 주소
    [선택]
    - 의료기관 소개, 사진, 복리후생
  다. 면허 인증 신청 시 (회원이 신청한 경우에 한함)
    - 면허증(또는 면허증명서) 이미지, 성명, 생년월일, 면허번호
  라. 급여 실태조사 등 설문 응답 시 (회원이 응답한 경우에 한함)
    - 직군, 경력, 근무 지역, 근무 조건, 급여 구간 등 회원이 입력한 응답 내용
  마. 서비스 이용 과정에서 자동 수집되는 항목
    - 접속 IP, 접속 로그, 쿠키, 기기 정보, 서비스 이용 기록
  바. 채용 과정에서 생성되는 약속 기록 (이용약관 제6조의2)
    - 확정된 근무·면접·첫 출근 약속의 일정과 진행 결과(근무 완료, 노쇼, 지각, 취소와 그 시점 등), 양쪽 회원의 답·설명, 회사의 확인 결과
    - 병/의원 회원이 지원서를 받고 처음 답한 시각

2. 개인정보의 수집·이용 목적
  - 회원 식별 및 본인 확인, 부정 이용 방지
  - 채용공고 검색·지원, 세미나 등록·신청, 커뮤니티 이용 등 서비스 제공
  - 이력서 작성·관리 및 매칭 추천
  - 고객 문의 및 민원 처리
  - 치과 종사자 면허 진위 확인 및 인증 배지 부여, 승인 시 회원정보(성명·생년월일·면허번호) 정정
  - 급여 실태조사 등 설문 응답의 비식별 통계 분석 및 리포트 발행(개인 응답은 공개하지 않음)
  - 서비스 개선을 위한 통계 분석(비식별 처리)
  - 약속 기록의 기록·확인 및 집계 결과 공개(이용약관 제6조의2): 최근 12개월 약속 기록 건수·비율과 병/의원 응답 시간(최근 6개월 평균)을 상대 회원(지원한 병/의원, 지원하려는 일반회원)에게 상대 이름·날짜 없이 제공
  - 법령에 따른 의무 이행

3. 개인정보의 보유·이용 기간
  - 회원 탈퇴 시 또는 수집·이용 목적 달성 시까지 보유합니다. 회원 탈퇴 후 30일 동안은 계정 복구를 위해 보관하고, 그 뒤 개인을 알아볼 수 있는 정보를 익명 처리합니다.
  - 면허 인증 이미지: 승인된 건은 사후 확인을 위해 승인일로부터 30일간 보관한 뒤 파기하고(관리자가 확인을 마치면 그 전에도 파기), 반려된 건은 반려 즉시 파기합니다. 검수 전 30일이 경과한 신청은 자동 파기합니다. 승인 시 성명·생년월일·면허번호를 회원정보에 반영하고 인증 결과와 함께 보관하며, 회원 탈퇴 시 삭제합니다.
  - 설문 응답: 비식별 통계로 처리한 뒤 집계 결과만 보관하며, 개인 식별이 가능한 원본 응답은 회원 탈퇴 시 삭제합니다.
  - 약속 기록: 공개 집계에는 최근 12개월 기록만 쓰며, 회원 탈퇴 시 상대 회원의 집계에서도 제외합니다. 다툼 확인을 위한 기록은 회원 탈퇴 시 또는 수집·이용 목적 달성 시까지 보유합니다.
  - 단, 관계 법령에 따라 일정 기간 보존이 필요한 정보는 아래와 같이 보관 후 파기합니다.
    · 계약 또는 청약철회 등에 관한 기록: 5년 (전자상거래법)
    · 대금결제 및 재화 등의 공급에 관한 기록: 5년 (전자상거래법)
    · 소비자 불만 또는 분쟁처리에 관한 기록: 3년 (전자상거래법)
    · 표시·광고에 관한 기록: 6개월 (전자상거래법)
    · 로그인 기록: 3개월 (통신비밀보호법)

4. 만 14세 미만 아동의 개인정보 처리
  회사는 만 14세 미만 아동의 회원가입을 받지 않습니다. 본인은 만 14세 이상임을 확인합니다.

5. 동의를 거부할 권리 및 거부에 따른 불이익
  회원은 본 동의를 거부할 권리가 있습니다. 다만 본 동의는 회원가입 및 서비스 이용에 필수적인 사항이므로, 동의를 거부하는 경우 회원가입이 제한됩니다.

6. 개인정보 처리에 관한 문의
  ${CONTACT_EMAIL}
        `.trim(),
    },
    {
        id: 'thirdParty',
        title: '개인정보 제3자 제공 동의',
        required: true,
        summary: '채용 지원·세미나 신청 시 해당 병/의원·운영자에게 이력서 등 개인정보를 제공합니다.',
        content: `
${SERVICE_NAME}(이하 "회사")는 「개인정보 보호법」 제17조에 따라 채용 지원·세미나 신청 시 아래와 같이 개인정보를 제3자에게 제공합니다.

1. 제공받는 자
  - 회원이 지원한 채용공고를 등록한 치과·의료기관
  - 회원이 신청한 세미나를 운영하는 강사 또는 운영 주체
  - 위 자 모두 회사와 별도의 개인정보 처리에 관한 약정을 체결한 자에 한합니다.

2. 제공받는 자의 개인정보 이용 목적
  - 채용 심사 및 합격·불합격 통보
  - 채용 면접 일정 안내 및 진행
  - 세미나 참가 자격 확인 및 참석자 관리
  - 세미나 진행에 관한 안내·공지

3. 제공하는 개인정보 항목
  가. 채용 지원 시
    - 이름, 휴대전화번호, 이메일, 생년월일, 성별, 거주 지역
    - 학력, 경력, 자격증·면허 정보, 희망 근무 조건(직무·지역·근무형태·급여), 자기소개서, 프로필 사진
    - 회원이 지원 시점에 작성·선택한 이력서 전체 내용
    - 명치사 약속 기록 집계(최근 12개월 확정된 약속 건수, 약속을 지킨 건수, 노쇼·지각·시작 24시간 안 취소 건수 — 이용약관 제6조의2, 상대 병/의원 이름·날짜 제외)
  나. 세미나 신청 시
    - 이름, 휴대전화번호, 이메일, 직무
    - 회원이 세미나 신청 시 입력한 추가 정보(있는 경우에 한함)

4. 제공받는 자의 보유·이용 기간
  - 채용 절차 또는 세미나 운영 완료 후 최대 3년
  - 단, 회원이 본 동의를 철회하거나 회원 탈퇴 시 제공받는 자에게 즉시 파기 요청합니다.
  - 관계 법령에 따른 보존 의무가 있는 경우 해당 기간 동안 보관 후 파기합니다.

5. 동의를 거부할 권리 및 거부에 따른 불이익
  회원은 본 동의를 거부할 권리가 있습니다. 다만 본 동의를 거부하는 경우 채용 지원, 세미나 신청 등 제3자 제공이 필요한 서비스 이용이 제한됩니다.

6. 제공받는 자의 의무
  회사는 제공받는 자가 제공받은 개인정보를 본 동의서에서 정한 목적 외로 이용하거나 제3자에게 재제공하지 못하도록 약정하며, 위반 시 즉시 제공을 중단합니다.
        `.trim(),
    },
    {
        id: 'sensitive',
        title: '민감정보(자격증·면허) 처리 동의',
        required: true,
        summary: '치과위생사·기공사·치과의사 등 자격증·면허 정보 처리에 대한 별도 동의입니다.',
        content: `
${SERVICE_NAME}(이하 "회사")는 「개인정보 보호법」 제23조에 따라 회원의 자격증·면허 정보를 민감정보에 준하여 처리하며, 아래 내용에 대해 별도의 동의를 받습니다.

1. 처리하는 민감정보 항목
  - 의료 관련 자격증 종류 (치과위생사, 치과기공사, 치과의사, 간호조무사 등)
  - 면허번호, 자격증 발급일, 면허증(또는 면허증명서) 이미지·성명·생년월일(회원이 면허 인증을 신청한 경우)

2. 처리 목적
  - 회원의 자격·면허 진위 확인 (전문가 라운지 등 자격 기반 서비스 이용 시)
  - 채용공고 지원 시 지원 자격 확인
  - 채용 매칭 추천의 정확도 향상
  - 부정 이용(타인 자격 도용 등) 방지

3. 보유·이용 기간
  - 회원 탈퇴 시 또는 자격 정보 삭제 요청 시까지 보유
  - 면허증(또는 면허증명서) 이미지는 승인된 건은 사후 확인을 위해 승인일로부터 30일간 보관한 뒤 파기하고(관리자가 확인을 마치면 그 전에도 파기), 반려된 건은 반려 즉시 파기합니다. 검수 전 30일이 경과한 신청은 자동 파기합니다. 그 밖의 예외적인 보존은 하지 않습니다.
  - 이미지 파기 후에는 인증 결과·면허번호·검수 이력(검수자·검수 시각)만 보관합니다.

4. 안전성 확보 조치
  - 면허번호 및 자격증 이미지는 암호화하여 저장합니다.
  - 자격 정보 열람·이용에 대한 내부 접근 권한을 최소화하고 접근 기록을 보관합니다.

5. 동의를 거부할 권리 및 거부에 따른 불이익
  회원은 본 동의를 거부할 권리가 있습니다. 다만 본 동의를 거부하는 경우 전문가 라운지(칼럼·뉴스·자료) 작성, 세미나 강사 신청, 일부 채용공고 지원 등 자격 기반 서비스 이용이 제한됩니다.
        `.trim(),
    },
    {
        id: 'resumePublic',
        title: '이력서·프로필 공개 동의',
        required: false,
        summary: '등록한 이력서를 채용 공고를 등록한 병/의원이 검색·열람할 수 있도록 공개합니다.',
        content: `
${SERVICE_NAME}(이하 "회사")는 회원이 등록한 이력서·프로필을 다음과 같이 채용 검색에 공개하기 위해 별도의 동의를 받습니다.

1. 공개되는 정보
  - 이력서 제목, 직무, 학력, 경력 요약, 자격증·면허 종류, 희망 근무 조건(지역·근무형태·급여), 자기소개서, 프로필 사진(등록한 경우)
  - 이름·휴대전화번호·이메일·정확한 주소 등 직접 식별 정보는 회원의 명시적 지원 또는 동의 전까지 비공개로 처리합니다.

2. 공개 대상
  - 회사와 채용 서비스 이용 계약을 체결한 병/의원 회원
  - 위 회원이 검색하거나 본인의 채용공고에 적합한 후보로 추천받은 경우 한정

3. 공개 기간
  - 동의일로부터 회원이 이력서 비공개 전환 또는 동의 철회 시까지
  - 회원 탈퇴 시 즉시 비공개 처리

4. 동의 철회 방법
  - 마이페이지 > 이력서 관리 > 공개/비공개 설정에서 언제든지 변경 가능
  - 일부 이력서만 공개로 설정하는 부분 공개도 가능합니다.

5. 동의를 거부할 권리 및 거부에 따른 불이익
  본 동의는 선택사항이며, 동의를 거부하셔도 회원가입 및 직접 지원 방식의 채용 서비스 이용에는 제한이 없습니다. 다만, 병/의원이 회원의 이력서를 먼저 검색·열람하여 채용 제안을 보내는 "역제안" 기능은 이용하실 수 없습니다.
        `.trim(),
    },
    {
        id: 'recommendation',
        title: '맞춤 추천 알고리즘 활용 동의',
        required: false,
        summary: 'AI가 회원의 이력서·활동 정보를 분석하여 맞춤 채용·세미나·콘텐츠를 추천합니다.',
        content: `
${SERVICE_NAME}(이하 "회사")는 회원에게 더 정확한 채용·세미나·콘텐츠 추천을 제공하기 위해 아래 내용에 대해 별도의 동의를 받습니다.

1. 활용하는 정보
  - 회원이 등록한 이력서 정보 (직무, 경력, 자격, 희망 조건)
  - 회원이 입력한 거주 지역 및 관심 지역 정보 (지역 기반 추천에 활용)
  - 회원의 서비스 이용 기록 (열람·검색·지원·즐겨찾기·세미나 신청 등)
  - 회원이 작성한 게시글 및 댓글의 관심 카테고리
  - 비식별 처리된 유사 회원군 데이터 (협업 필터링용)
  ※ 본 서비스는 GPS 등 실시간 위치정보를 수집하지 않으며, 회원이 직접 입력한 지역 정보만 활용합니다.

2. 활용 목적 (자동화된 결정 포함)
  - 맞춤 채용공고 추천 ("AI 추천 채용")
  - 등록 지역 기반 가까운 채용·세미나 추천
  - 맞춤 세미나·전문가 라운지 콘텐츠 추천
  - 신규 가입 후보자를 병/의원 회원에게 추천 (이력서 공개에 동의한 경우에 한함)
  - 부정 이용·중복 가입 자동 탐지

3. 추천 결과에 대한 회원의 권리
  - 회원은 자동화된 추천 결과에 대해 설명을 요구하거나 이의를 제기할 수 있습니다.
  - 회원은 본 동의를 철회하여 추천 알고리즘에서 자신의 정보를 제외시킬 수 있습니다.
  - 본 추천은 참고용이며, 최종적인 지원·신청 결정은 회원이 합니다.

4. 보유·이용 기간
  - 동의일로부터 회원 탈퇴 시까지 또는 회원이 동의를 철회할 때까지

5. 동의 철회 방법
  - 마이페이지 > 내 정보 수정(병·의원 회원은 치과 정보 수정) > 약관·동의 설정 > 맞춤 추천 알고리즘 활용 동의에서 언제든지 변경 가능
  - 철회하면 이후의 홈 화면 구성·AI 질문 추천 문구 등에 서비스 이용 기록을 쓰지 않습니다.

6. 동의를 거부할 권리 및 거부에 따른 불이익
  본 동의는 선택사항이며, 동의를 거부하시더라도 회원가입 및 ${SERVICE_NAME} 서비스 이용에는 제한이 없습니다. 다만 맞춤 추천이 제공되지 않으며 기본 정렬(최신순·인기순) 기준으로 콘텐츠가 노출됩니다.
        `.trim(),
    },
    {
        id: 'pushAdvertising',
        title: '앱 푸시 광고성 알림 수신 동의',
        required: false,
        hiddenInProfile: true,
        summary: '명치사 앱에서 치과계 뉴스 브리핑, 이벤트·혜택 소식을 푸시 알림으로 받아보실 수 있습니다.',
        content: `
${SERVICE_NAME}(이하 "회사")는 「정보통신망 이용촉진 및 정보보호 등에 관한 법률」 제50조에 따라 광고성 정보를 앱 푸시 알림으로 전송하기 위해 아래 내용에 대해 별도의 동의를 받습니다.

1. 수신 정보의 종류
  - 명치사 브리핑: 매일 아침 전날 치과계 핵심 뉴스 요약
  - 이벤트·혜택: 세미나 할인, 이벤트, 프로모션 소식

2. 수신 방법
  - 명치사 앱 푸시 알림 (앱 설치 후 기기의 알림 권한을 허용한 경우에만 수신됩니다)
  - 이메일·문자·카카오톡으로는 발송하지 않습니다. 이메일 브리핑은 마이페이지에서 별도로 설정합니다.

3. 보유·이용 기간
  - 동의일로부터 회원 탈퇴 시까지 또는 회원이 수신 거부 의사를 표시할 때까지

4. 동의 철회 방법
  - 마이페이지 > 내 정보 수정 > 푸시 설정에서 항목별로 언제든지 끌 수 있습니다
  - 고객센터(${CONTACT_EMAIL})를 통한 신청

5. 야간 광고성 정보 발송
  - 오후 9시부터 다음 날 오전 8시까지의 광고성 푸시는 마이페이지 > 푸시 설정에서 "야간 수신"에 별도로 동의한 회원에게만 발송합니다. 본 동의만으로는 야간에 발송하지 않습니다.

6. 동의를 거부할 권리 및 거부에 따른 불이익
  본 동의는 선택사항이며, 동의를 거부하시더라도 ${SERVICE_NAME} 서비스 이용에는 제한이 없습니다. 거부 시에도 서비스 이용에 필요한 안내 알림(세미나 신청 결과, 내 활동 알림 등)은 마이페이지 > 푸시 설정에서 따로 관리할 수 있습니다.
        `.trim(),
    },
];
